import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { FaBriefcase, FaClipboardList, FaMapMarkerAlt, FaStar } from "react-icons/fa";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import "../../assets/styles/buyer_recruit_external_qa.scss";

const INDUSTRIES_SERVED_ID_TO_LABEL = {
  1: "Electronics",
  2: "Textiles",
  3: "Food & Beverage",
  4: "Pharmaceuticals",
  5: "Automotive",
};

function asNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

function fmtReviews(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x < 0) return "0";
  return x.toLocaleString();
}

function fmtCompleted(n) {
  if (n == null || n === "") return "—";
  const x = Number(n);
  if (!Number.isFinite(x) || x < 0) return "—";
  return x.toLocaleString();
}

export default function BuyerRecruitExternalQaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { rfqId: rfqIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("companyId");
  const quoteIdFromQuery = searchParams.get("quoteId");

  /** CM RFQ row id: useParams + pathname fallback (RR7 / pathless layout edge cases). */
  const rfqId = useMemo(() => {
    let n = asNum(rfqIdParam);
    if (Number.isFinite(n) && n > 0) return n;
    const m = location.pathname.match(/\/recruit-external-qa\/([^/]+)\/?$/);
    if (m?.[1]) {
      n = asNum(decodeURIComponent(String(m[1]).trim()));
      if (Number.isFinite(n) && n > 0) return n;
    }
    return NaN;
  }, [rfqIdParam, location.pathname]);
  const cmQuotationIdFromUrl = useMemo(() => asNum(quoteIdFromQuery), [quoteIdFromQuery]);
  const [fallbackCmQuotationId, setFallbackCmQuotationId] = useState(null);
  const [resolvingCmQuotationId, setResolvingCmQuotationId] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inspectors, setInspectors] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const effectiveCmQuotationId = useMemo(() => {
    if (Number.isFinite(cmQuotationIdFromUrl) && cmQuotationIdFromUrl > 0) return cmQuotationIdFromUrl;
    if (fallbackCmQuotationId != null && Number.isFinite(fallbackCmQuotationId) && fallbackCmQuotationId > 0) {
      return fallbackCmQuotationId;
    }
    return NaN;
  }, [cmQuotationIdFromUrl, fallbackCmQuotationId]);

  useEffect(() => {
    if (!Number.isFinite(rfqId) || rfqId <= 0) return;
    if (Number.isFinite(cmQuotationIdFromUrl) && cmQuotationIdFromUrl > 0) {
      setFallbackCmQuotationId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setResolvingCmQuotationId(true);
        const subRes = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.MY_RFQ_SUBMISSIONS, {
          limit: 200,
          offset: 0,
        });
        const inner = subRes?.data;
        const rows = Array.isArray(inner?.items) ? inner.items : [];
        const row = rows.find((r) => String(r?.id) === String(rfqId));
        const qt = row?.quote_table_id ?? row?.quoteTableId;
        const n = qt != null ? Number(qt) : NaN;
        if (!cancelled) {
          setFallbackCmQuotationId(Number.isFinite(n) && n > 0 ? n : null);
        }
      } catch {
        if (!cancelled) setFallbackCmQuotationId(null);
      } finally {
        if (!cancelled) setResolvingCmQuotationId(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rfqId, cmQuotationIdFromUrl]);

  const loadInspectors = useCallback(async () => {
    if (!Number.isFinite(rfqId) || rfqId <= 0) {
      toast.error("Invalid RFQ id");
      navigate(`/dashboard/buyer/contract-manufacturers/quotes${companyId ? `?companyId=${companyId}` : ""}`);
      return;
    }
    try {
      setLoading(true);
      const res = await CommonService.get(API_URLS.QA.QUALITY_INSPECTORS_BY_LOCATION());
      const list = res?.data?.data ?? res?.data ?? [];
      setInspectors(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load quality inspectors");
      setInspectors([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate, rfqId]);

  useEffect(() => {
    loadInspectors();
  }, [loadInspectors]);

  const selectedInspector = useMemo(
    () => inspectors.find((x) => Number(x?.quality_inspector_id) === Number(selectedId)) || null,
    [inspectors, selectedId]
  );

  const handleProceed = useCallback(async () => {
    if (!selectedInspector) {
      toast.error("Please select one QA");
      return;
    }
    if (resolvingCmQuotationId) {
      toast.info("Loading quotation details…");
      return;
    }
    if (!Number.isFinite(effectiveCmQuotationId) || effectiveCmQuotationId <= 0) {
      toast.error(
        "No submitted quotation found for this RFQ. Submit a manufacturer quote first, then try again."
      );
      return;
    }
    try {
      setSaving(true);
      await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.QC_EXTERNAL_EVALUATOR_DECISION(rfqId), {
        decision: "yes",
        selectedQualityInspectorId: selectedInspector.quality_inspector_id,
      });

      const qs = new URLSearchParams();
      qs.set("quotation_id", String(effectiveCmQuotationId));
      qs.set("rfq_id", String(rfqId));
      if (companyId) qs.set("companyId", String(companyId));
      qs.set("fromRecruit", "1");
      navigate(`/buyer-qa-request?${qs.toString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to save selection");
    } finally {
      setSaving(false);
    }
  }, [companyId, effectiveCmQuotationId, navigate, resolvingCmQuotationId, rfqId, selectedInspector]);

  const quotesHref = `/dashboard/buyer/contract-manufacturers/quotes${companyId ? `?companyId=${companyId}` : ""}`;

  return (
    <div className="extqa-page">
      <header className="extqa-page-header">
        <nav className="extqa-breadcrumb" aria-label="Breadcrumb">
          <ol className="extqa-breadcrumb__list">
            <li className="extqa-breadcrumb__item">
              <Link to="/dashboard/buyer/contract-manufacturers" className="extqa-breadcrumb__link">
                Contract manufacturers
              </Link>
            </li>
            <li className="extqa-breadcrumb__sep" aria-hidden>
              /
            </li>
            <li className="extqa-breadcrumb__item">
              <Link to={quotesHref} className="extqa-breadcrumb__link">
                Quotes
              </Link>
            </li>
            <li className="extqa-breadcrumb__sep" aria-hidden>
              /
            </li>
            <li className="extqa-breadcrumb__item extqa-breadcrumb__item--current" aria-current="page">
              Recruit external QA
            </li>
          </ol>
        </nav>
        <div className="extqa-page-heading">
          <h1 className="extqa-page-title">Recruit external QA</h1>
          <p className="extqa-page-subtitle">
            Choose a verified quality inspector for your contract manufacturing RFQ
            {Number.isFinite(rfqId) && rfqId > 0 ? ` (RFQ #${rfqId})` : ""}.
          </p>
        </div>
      </header>

      <div className="extqa-top">
        <div className="extqa-search-wrap">
          <span className="extqa-search-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3-3" strokeLinecap="round" />
            </svg>
          </span>
          <input className="extqa-search" type="search" placeholder="Search agencies by name or location..." disabled />
        </div>
        <div className="extqa-actions">
          <button type="button" className="extqa-btn extqa-btn--ghost" disabled>
            Filter by Certification
          </button>
          <button type="button" className="extqa-btn extqa-btn--ghost" disabled>
            Sort by Rating
          </button>
        </div>
      </div>

      {loading ? <div className="extqa-loading text-muted">Loading inspectors...</div> : null}
      {!loading && inspectors.length === 0 ? (
        <div className="alert alert-info">No quality inspectors found.</div>
      ) : null}

      <div className="extqa-list">
        {inspectors.map((qa) => {
          const id = qa?.quality_inspector_id;
          const checked = Number(selectedId) === Number(id);
          const name = qa?.company_name || qa?.name || "Quality Inspector";
          const city = qa?.city_name || "";
          const country = qa?.country_name || "";
          const location =
            [city, country].filter(Boolean).join(", ") ||
            [qa?.state_name, country].filter(Boolean).join(", ") ||
            "";
          const hasRating = qa?.rating != null && Number.isFinite(Number(qa.rating));
          const rating = hasRating ? Number(qa.rating) : null;
          const hasReviews = qa?.reviews_count != null && Number.isFinite(Number(qa.reviews_count));
          const reviews = hasReviews ? Number(qa.reviews_count) : null;
          const completed = qa?.completed_inspections;
          const certList = Array.isArray(qa?.certifications) ? qa.certifications.filter(Boolean) : [];
          const industriesList = Array.isArray(qa?.industries_served)
            ? qa.industries_served
                .filter((x) => x != null && String(x).trim() !== "")
                .map((x) => {
                  const raw = String(x).trim();
                  const n = Number(raw);
                  if (Number.isFinite(n) && INDUSTRIES_SERVED_ID_TO_LABEL[n]) return INDUSTRIES_SERVED_ID_TO_LABEL[n];
                  return raw;
                })
            : Array.isArray(qa?.industriesServed)
              ? qa.industriesServed.filter(Boolean).map((x) => String(x).trim()).filter(Boolean)
              : [];
          const specList = Array.isArray(qa?.specialties) ? qa.specialties.filter(Boolean) : [];
          const inspectionTypesList = Array.isArray(qa?.inspection_types)
            ? qa.inspection_types.filter(Boolean)
            : [];
          const inspectionTypesDisplay = inspectionTypesList.length ? inspectionTypesList.join(", ") : "—";
          const industriesDisplay = industriesList.length ? industriesList.join(", ") : "—";

          return (
            <div key={String(id)} className="extqa-card">
              <div className="extqa-card__body">
                <div className="extqa-card__top">
                  <div className="extqa-card__identity">
                    <div className="extqa-avatar" aria-hidden>
                      {String(name).trim().slice(0, 1).toUpperCase()}
                    </div>
                    <div className="extqa-card__info">
                      <h3 className="extqa-name">{name}</h3>
                      <div className="extqa-rating-line">
                        <FaStar className="extqa-icon-star" aria-hidden />
                        {rating != null ? (
                          <>
                            <span className="extqa-rating-num">{rating.toFixed(1)}</span>
                            <span className="extqa-rating-reviews">
                              ({reviews != null ? `${fmtReviews(reviews)} reviews` : "—"})
                            </span>
                          </>
                        ) : (
                          <span className="extqa-rating-reviews">—</span>
                        )}
                      </div>
                      <div className="extqa-loc-line">
                        <FaMapMarkerAlt className="extqa-icon-pin" aria-hidden />
                        <span>{location || "—"}</span>
                      </div>
                    </div>
                  </div>
                  <label className={`extqa-pick ${checked ? "extqa-pick--checked" : ""}`}>
                    <input
                      type="checkbox"
                      className="extqa-pick__input"
                      checked={checked}
                      onChange={() => setSelectedId(checked ? null : id)}
                    />
                    <span className="extqa-pick__box" aria-hidden />
                    <span className="extqa-pick__label">Select for RFQ</span>
                  </label>
                </div>

                <div className="extqa-metrics">
                  <div className="extqa-metrics__col">
                    <div className="extqa-metrics__label">Inspection Types</div>
                    <div className="extqa-metrics__value extqa-metrics__value--wrap">
                      <FaClipboardList className="extqa-metrics__icon" aria-hidden />
                      <span>{inspectionTypesDisplay}</span>
                    </div>
                  </div>
                  <div className="extqa-metrics__col">
                    <div className="extqa-metrics__label">Industries served</div>
                    <div className="extqa-metrics__value extqa-metrics__value--wrap">
                      <FaBriefcase className="extqa-metrics__icon" aria-hidden />
                      <span>{industriesDisplay}</span>
                    </div>
                  </div>
                  <div className="extqa-metrics__col">
                    <div className="extqa-metrics__label">Completed Inspections</div>
                    <div className="extqa-metrics__value extqa-metrics__value--plain">{fmtCompleted(completed)}</div>
                  </div>
                </div>

                <div className="extqa-tags-block">
                  <div className="extqa-tag-row">
                    <span className="extqa-tag-heading">Certifications</span>
                    <div className="extqa-pills extqa-pills--cert">
                      {certList.length ? (
                        certList.map((t, i) => (
                          <span key={`c-${i}-${t}`} className="extqa-pill extqa-pill--cert">
                            {t}
                          </span>
                        ))
                      ) : (
                        <span className="extqa-tag-empty">—</span>
                      )}
                    </div>
                  </div>
                  <div className="extqa-tag-row">
                    <span className="extqa-tag-heading">Specialties</span>
                    <div className="extqa-pills extqa-pills--spec">
                      {specList.length ? (
                        specList.map((t, i) => (
                          <span key={`s-${i}-${t}`} className="extqa-pill extqa-pill--spec">
                            {t}
                          </span>
                        ))
                      ) : (
                        <span className="extqa-tag-empty">—</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className={`extqa-footer ${selectedInspector ? "extqa-footer--active" : ""}`}>
        <div className="extqa-footer__left">
          <strong>{selectedInspector ? "1 Agency Selected" : "No Agency Selected"}</strong>
          <div className="extqa-footer__sub">
            {selectedInspector ? "Ready to request quotations. Proceed to the RFQ form." : "Select one QA to continue."}
          </div>
        </div>
        <div className="extqa-footer__right">
          <button
            type="button"
            className="extqa-btn extqa-btn--primary"
            disabled={!selectedInspector || saving}
            onClick={handleProceed}
          >
            Proceed to RFQ Form
          </button>
          <button
            type="button"
            className="extqa-btn extqa-btn--outline"
            disabled={saving}
            onClick={() => navigate(`/dashboard/buyer/contract-manufacturers/quotes${companyId ? `?companyId=${companyId}` : ""}`)}
          >
            Clear Selection
          </button>
        </div>
      </div>
    </div>
  );
}
